import NextLink from 'next/link';
import {Box, BoxProps, Button, Center, Flex, Heading, Image, Text} from '@chakra-ui/react';
import {useIntl} from 'react-intl';
import dayjs from 'dayjs';

import {LikeIcon} from '@eksab/assets/icons';
import {getCountry} from '@eksab/util';

import {ArticleBadges} from './';
import {getLeagues} from '../helpers';
import type {Article} from '../types';
import {useTranslate} from '@eksab/i18n';
import {useState} from 'react';
import {UnlockModal} from './UnlockModal';
import {LockIcon} from './LockIcon';
import {useRouter} from 'next/router';
import {useMeQuery} from '@eksab/hooks';
import {AuthModal} from '@eksab/features/auth/modals/AuthModal';

interface ArticleCardProps extends BoxProps {
  article: Article;
}

export const ArticleCard = ({article, ...props}: ArticleCardProps) => {
  const format = useIntl().formatNumber;
  const router = useRouter();

  const leagues = getLeagues(article);
  const locale = `ar-${getCountry(router.locale)}`;
  const publishedAt =
    dayjs.duration(dayjs().diff(article.published_at)).asDays() > 2
      ? dayjs(article.published_at).format(dayjs().year() - dayjs(article.published_at).year() ? 'D MMM YYYY' : 'D MMM')
      : dayjs().to(article.published_at);

  return (
    <NextLink href={`/news/${article.slug}`} locale={locale} passHref legacyBehavior>
      <Box
        as="a"
        pos="relative"
        display="flex"
        flexDir="column"
        rounded="md"
        shadow="xs"
        overflow="hidden"
        w="full"
        {...props}
      >
        {article.is_premium && (
          <PremiumView
            is_locked={article.is_locked}
            news_id={article.id}
            onUnlock={() => router.push(`/news/${article.slug}`, undefined, {locale})}
          />
        )}
        <Box flexBasis="50%">
          <Image w="full" h="13.875rem" objectFit="cover" src={article.image?.original} alt={article.title} />
        </Box>

        <Box display="flex" flexDir="column" flexBasis="50%">
          <Text px="5" py="1.5" flex={1} mb="2.5" fontSize="sm" bg={leagues ? 'gray.600' : 'inherit'}>
            {leagues}
          </Text>

          <Heading px="5" mb="3.5" size="md" lineHeight="1.7" noOfLines={2}>
            {article.title}
          </Heading>

          <Flex px="5" pb="3.5" align="flex-end" justify="space-between" flex={1} color="black">
            <Text fontSize="sm">{publishedAt}</Text>

            {!!article.likes && (
              <Text display="flex" alignItems="center" gap="1.5" fontSize="sm">
                {format(article.likes)}
                <LikeIcon />
              </Text>
            )}
          </Flex>
        </Box>

        <ArticleBadges sponsor={article.sponsor} />
      </Box>
    </NextLink>
  );
};

const PremiumView = ({is_locked, news_id, onUnlock}: {is_locked: boolean; news_id: number; onUnlock: () => void}) => {
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const me = useMeQuery().data;
  const t = useTranslate();

  return (
    <>
      <UnlockModal
        isOpen={unlockModalOpen}
        onClose={() => setUnlockModalOpen(false)}
        news_id={news_id}
        onUnlock={onUnlock}
      />
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => {
          setAuthModalOpen(false);
        }}
      />
      <Center bgColor={is_locked ? 'rgba(16, 16, 16, 0.5)' : 'transparent'} pos="absolute" inset={0}>
        <Flex
          bg="linear-gradient(89.99deg, #EC4B72 0%, #F4A771 100%)"
          py="2"
          px="4"
          align="center"
          pos="absolute"
          top={4}
          rounded="full"
          insetStart={4}
        >
          <Image src="/images/star.png" boxSize="28px" me="2" alt="star" />
          <Text color="white" fontWeight="500">
            {t('news.premium.content')}
          </Text>
        </Flex>
        {is_locked && (
          <Button
            display="flex"
            gap="3"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              me ? setUnlockModalOpen(true) : setAuthModalOpen(true);
            }}
          >
            <LockIcon />
            {t('news.premium.cta')}
          </Button>
        )}
      </Center>
    </>
  );
};
